import type { BoardResponse } from '../../services/api/board';
import { formatDayMonth as fmtDayMonth } from '../../utils/dateFormat';

interface CountSet {
  total: number;
  unassigned: number;
  byMilestoneId: Map<number, number>;
}

/**
 * Phase 30 REQ-07 / Phase 31 GOAL-06 — count formula excludes the highest-`ordering`
 * column (the convention for "Done" in this codebase; no `isTerminal` flag exists).
 * Extracted from MilestoneSidebar.tsx (Phase 30) per Phase 31 CONTEXT D-18 so the
 * count contract survives the sidebar deletion in Plan 04.
 */
export function computeMilestoneCounts(board: BoardResponse | undefined): CountSet {
  if (!board || board.columns.length === 0) {
    return { total: 0, unassigned: 0, byMilestoneId: new Map() };
  }
  const maxOrdering = Math.max(...board.columns.map((c) => c.ordering));
  let total = 0;
  let unassigned = 0;
  const byMilestoneId = new Map<number, number>();
  for (const col of board.columns) {
    if (col.ordering === maxOrdering) continue; // exclude highest-ordering ("Done") per D-03 / REQ-07
    for (const t of col.tasks) {
      total += 1;
      if (t.milestoneId === null) {
        unassigned += 1;
      } else {
        byMilestoneId.set(t.milestoneId, (byMilestoneId.get(t.milestoneId) ?? 0) + 1);
      }
    }
  }
  return { total, unassigned, byMilestoneId };
}

/**
 * Format a milestone's date range as `20.11 – 05.12` (en-dash, year-less).
 * Extracted verbatim from MilestoneSidebar.tsx:66-73; MKS-351 moved the
 * day/month rendering itself onto the shared date util.
 */
export function formatDateRange(startDate: string | null, endDate: string | null): string | null {
  if (!startDate && !endDate) return null;
  if (startDate && endDate) {
    return `${fmtDayMonth(startDate)} – ${fmtDayMonth(endDate)}`;
  }
  if (startDate) return `od ${fmtDayMonth(startDate)}`;
  return `do ${fmtDayMonth(endDate!)}`;
}
